import React, { useRef, useEffect } from 'react';
import { useController } from 'react-hook-form';
import { v4 } from 'uuid';

/**
 * A GDS (GEICO Design System) SelectBox component that integrates with React Hook Form.
 * This component uses the new gds-select-box-wrapper web component and provides form validation
 * and state management through react-hook-form.
 * 
 * @param {Object} props - The component props
 * @param {Object} props.control - React Hook Form control object for form state management
 * @param {string} [props.name="SelectBox"] - The field name for form registration and validation
 * @param {Array<{value: string|number, label?: string, text?: string}>} [props.options=[]] - Array of option objects for the select box
 * @param {string} [props.label="Select Box"] - Label text to display above the select box
 * @param {string} [props.description] - Optional description text to display below the label
 * @param {string} [props.placeholder] - Optional placeholder text for the empty option
 * 
 * @returns {JSX.Element} The rendered SelectBox component
 * 
 * @example
 * // Basic usage with options
 * <SelectBox
 *   control={control}
 *   name="country"
 *   label="Select Country"
 *   placeholder="Choose a country"
 *   options={[
 *     { value: 'us', label: 'United States' },
 *     { value: 'ca', label: 'Canada' }
 *   ]}
 * />
 * 
 * @example
 * // With description and validation error display
 * <SelectBox
 *   control={control}
 *   name="state"
 *   label="State"
 *   description="Select your state of residence"
 *   options={stateOptions}
 * />
 */
export default function SelectBox({
    control,
    name = "SelectBox",
    options = [],
    label = "Select Box",
    description,
    placeholder
}) {
    /** @type {React.RefObject<HTMLSelectElement>} Reference to the native select DOM element */
    const selectBoxRef = useRef();

    /** @type {React.RefObject<string>} Unique ID for the select box element */
    const selectId = useRef(`select-box-${v4()}`);

    /**
     * React Hook Form controller that provides field state and handlers
     * @type {Object}
     * @property {Object} field - Field handlers and value
     * @property {Function} field.onChange - Handler for value changes
     * @property {Function} field.onBlur - Handler for blur events
     * @property {any} field.value - Current field value
     * @property {Object} fieldState - Field validation state
     * @property {Object} fieldState.error - Current validation error (if any)
     */
    const {
        field: { onChange, onBlur, value },
        fieldState: { error }
    } = useController({
        name,
        control
    });

    /**
     * Effect hook to set up event listeners for the native select element within gds-select-box-wrapper.
     * Handles the standard 'change' event and standard 'blur' event.
     * Cleans up event listeners on component unmount or dependency changes.
     * 
     * @effect
     * @listens change - Standard change event fired when select value changes
     * @listens blur - Standard blur event for field validation
     */
    useEffect(() => {
        /**
         * Handles the native select change event and updates the form field value
         * @param {Event} e - The standard change event from the select element
         */
        const handleSelectBoxChange = (e) => {
            onChange(e.detail?.value || e.target.value);
        };

        const selectBoxElement = selectBoxRef.current;

        if (selectBoxElement) {
            selectBoxElement.addEventListener("change", handleSelectBoxChange);
            selectBoxElement.addEventListener("blur", onBlur);

            return () => {
                selectBoxElement.removeEventListener("change", handleSelectBoxChange);
                selectBoxElement.removeEventListener("blur", onBlur);
            };
        }
    }, [onChange, onBlur]);

    return (
        <gds-select-box-wrapper>
            {/* Conditionally render label if provided */}
            {label && (
                <gds-label-legend slot="label" element="label" for={selectId.current}>
                    {label}

                    {description && (
                        <span slot="description">{description}</span>
                    )}
                </gds-label-legend>
            )}

            <select slot='select' id={selectId.current} name={name} ref={selectBoxRef}>
                {/* Conditionally render placeholder as empty option if provided */}
                {placeholder && <option value="">{placeholder}</option>}

                {/* Render all option elements from the options array */}
                {options.map(option => (
                    <option
                        key={option.value}
                        value={option.value}
                        selected={value === option.value}
                    >
                        {/* Display label if available, otherwise fall back to text */}
                        {option.label || option.text}
                    </option>
                ))}
            </select>

            {/* Conditionally render error message if validation error exists */}
            {error &&
                <span slot="error-message">{error.message}</span>
            }
        </gds-select-box-wrapper>
    );
}